"use client";

import { type CreateTeamInput, CreateTeamSchema } from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	EmojiPicker,
	EmojiPickerContent,
	EmojiPickerFooter,
	EmojiPickerSearch,
} from "@/components/ui/emoji-picker";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

interface CreateTeamDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onTeamCreated?: () => void;
}

export function CreateTeamDialog({
	open,
	onOpenChange,
}: CreateTeamDialogProps) {
	const [isLoading, setIsLoading] = React.useState(false);
	const [isOpen, setIsOpen] = React.useState(false);
	const { teams, refresh } = useTeam();
	const parentTeams = teams.filter((team) => team.type === "parent");

	const form = useForm<CreateTeamInput>({
		resolver: zodResolver(CreateTeamSchema),
		defaultValues: {
			name: "",
			emoji: "",
			type: "personal",
			parentID: undefined,
			email: "",
			url: "",
		},
	});

	async function onSubmit(data: CreateTeamInput) {
		try {
			setIsLoading(true);
			const response = await requestWithHandling<void>(() =>
				apiClient.api.teamCreate({
					name: data.name,
					emoji: encodeURIComponent(data.emoji),
					type: data.type,
					parent_id: data.parentID,
					email: data.email,
					url: data.url,
				}),
			);

			if (response.code !== "SUCCESS") {
				toast.error("We couldn't create the team.", {
					description: response.message,
				});
				return;
			}

			toast.success("Team created successfully", {
				description: "You can now start collaborating with your team.",
			});

			form.reset();
			onOpenChange(false);
			await refresh();
		} catch (error) {
			toast.error("Failed to create team", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Create Team</DialogTitle>
					<DialogDescription>
						Create a new team to collaborate with others.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Name</FormLabel>
										<FormControl>
											<Input
												placeholder="Enter team name..."
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="emoji"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Emoji</FormLabel>
										<FormControl>
											<Popover onOpenChange={setIsOpen} open={isOpen}>
												<PopoverTrigger className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-input bg-background text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 w-full justify-start">
													{field.value ? (
														<span className="text-2xl">{field.value}</span>
													) : (
														"Select emoji..."
													)}
												</PopoverTrigger>
												<PopoverContent className="w-full p-0 h-72">
													<EmojiPicker
														onEmojiSelect={(emoji) =>
															field.onChange(emoji.emoji)
														}
													>
														<EmojiPickerSearch />
														<EmojiPickerContent />
														<EmojiPickerFooter />
													</EmojiPicker>
												</PopoverContent>
											</Popover>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="url"
								render={({ field }) => (
									<FormItem>
										<FormLabel>URL</FormLabel>
										<FormControl>
											<Input
												placeholder="Enter team URL..."
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												placeholder="Enter team email..."
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="flex gap-4">
							<FormField
								control={form.control}
								name="type"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>Team Type</FormLabel>
										<Select
											disabled={isLoading}
											onValueChange={field.onChange}
											value={field.value}
										>
											<FormControl>
												<SelectTrigger className="w-full">
													<SelectValue>
														{field.value
															? field.value.charAt(0).toUpperCase() +
																field.value.slice(1)
															: "Select team type"}
													</SelectValue>
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="parent">Parent</SelectItem>
												<SelectItem value="child">Child</SelectItem>
												<SelectItem value="personal">Personal</SelectItem>
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							{form.watch("type") === "child" && (
								<FormField
									control={form.control}
									name="parentID"
									render={({ field }) => (
										<FormItem className="flex-1">
											<FormLabel>Parent Team</FormLabel>
											<Select
												disabled={isLoading}
												onValueChange={field.onChange}
												defaultValue={field.value}
											>
												<FormControl>
													<SelectTrigger className="w-full">
														<SelectValue>
															{field.value
																? parentTeams.find((t) => t.id === field.value)
																		?.name || "Select parent team"
																: "Select parent team"}
														</SelectValue>
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{parentTeams?.map((team) => (
														<SelectItem key={team.id} value={team.id}>
															{team.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
						<DialogFooter>
							<Button
								type="submit"
								disabled={isLoading}
								className="w-full sm:w-auto"
							>
								{isLoading ? "Creating..." : "Create Team"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
